import Flex from 'typescript-styled-flex'
import { withTranslation, WithTranslation } from 'react-i18next'
import { FieldArray } from 'redux-form'
import React from 'react'
import { FiSettings } from 'react-icons/fi'
import { RangeType } from '../../../components/FormBuilder/DateRangeInput'
import { Desktop } from '../../../components/MediaQuery'
import { DefaultStatusRedux } from '../Settings/DefaultStatus'
import {
  DateInput,
  FormGroup,
  NumberInput,
  SquareCheckbox,
  TimeInput,
} from '../../../components/FormBuilder'
import { GroupTitle } from '../../../components/FormBuilder/FormElement'
import ExpandableSection from '../../../components/Form/ExpandableSection'
import { PrepaidType } from '../../../models/Object'
import { withSize } from '../../../hooks/useSize'
import { IWithSize } from '../../BookingCalendar/Calendar/Calendar'
import DateRangeInputContainer from './Components/DateRangeContainerWithReservationFields'
import { ICreateReservationFormValues } from './CreateReservation'
import { ChildrenAge } from './ChildrenAge'
import Services from './Services'
import {
  ColorInputWithColors,
  LocalTaxInput,
  MainInputWithCurrency,
  PriceInputWithCurrency,
  RoomSelect,
  SelectWithDictionary,
} from './Components'
import PriceInputWithToolTip from './Components/PriceInputWithToolTip'
import VisibilityControlled from './Components/VisibilityControlled'
import { withIsDesktop, withIsHorizontal } from './withIsDesktop'
import { FormNames } from './index'

class ReservationTab extends React.Component<
  { isDesktop?: boolean; isHorizontal?: boolean } & WithTranslation &
    IReservationTabProps &
    IReservationTabDispatch &
    IWithSize,
  { countOfChildrens: number; countOfAdults: number }
> {
  public constructor(props) {
    super(props)
    this.state = {
      countOfAdults: this.props.initialValues.adultsCount,
      countOfChildrens: this.props.initialValues.childrenCount,
    }
  }

  public shouldComponentUpdate(
    nextProps: Readonly<
      { isDesktop?: boolean; isHorizontal?: boolean } & WithTranslation &
        IReservationTabProps &
        IReservationTabDispatch &
        IWithSize
    >,
    nextState: Readonly<{ countOfChildrens: number; countOfAdults: number }>,
  ): boolean {
    if (
      nextProps.timeFrom !== this.props.timeFrom ||
      nextProps.airbnb !== this.props.airbnb ||
      nextProps.timeTo !== this.props.timeTo ||
      nextProps.type !== this.props.type ||
      nextProps.allowReservationEdit !== this.props.allowReservationEdit ||
      nextProps.roomSize !== this.props.roomSize ||
      nextProps.formatDate !== this.props.formatDate ||
      nextProps.prepaidType !== this.props.prepaidType ||
      nextProps.allowConfig !== this.props.allowConfig ||
      nextState.countOfAdults !== this.state.countOfAdults ||
      nextState.countOfChildrens !== this.state.countOfChildrens ||
      nextProps.isDesktop !== this.props.isDesktop ||
      nextProps.isSaving !== this.props.isSaving ||
      nextProps.size !== this.props.size
    ) {
      return true
    } else {
      return false
    }
  }

  public checkCountOfAdults = e => {
    this.setState({ ...this.state, countOfAdults: e })
  }

  public onChange = e => {
    this.props.changeChildren(e)
    this.setState({ ...this.state, countOfChildrens: e })
  }

  public render(): React.ReactNode {
    const {
      isDesktop,
      change,
      changeDates,
      timeTo,
      timeFrom,
      roomSize,
      initialValues,
      formatDate,
      t,
      type,
      calculate,
      refresh,
      size,
      airbnb,
      prepaidType,
      toggleServicesModal,
      isSaving,
      firePricingError,
      allowConfig,
      allowReservationEdit,
      isLite,
    } = this.props
    const { countOfChildrens, countOfAdults } = this.state

    const isMobile = !isDesktop

    function redirect() {
      window.open(`/pricing`, '_blank')
    }

    function goToAdvanceSettings() {
      if (allowConfig) {
        window.open(`/price/prepayment`, '_blank')
      } else {
        firePricingError()
      }
    }

    function changeDate(name) {
      return function(e) {
        changeDates(name, e)
      }
    }

    function onTimeClick(e) {
      if (!e.currentTarget.value) {
        change(e.name, e.name === 'timeTo' ? timeTo : timeFrom)
      }
    }

    function countOfPersonsIsToLarge() {
      return !!(countOfAdults + countOfChildrens > Number(roomSize) && roomSize)
    }

    return (
      <Flex
        row={isDesktop}
        column={!isDesktop}
        full={true}
        style={{
          height: 'auto',
          padding: !isMobile
            ? `${10 * size}px ${20 * size}px ${16 * size}px`
            : `${10 * size}px ${16 * size}px ${25 * size}px ${16 * size}px`,
          width: '100%',
        }}
      >
        <Flex column={true} full={true}>
          <FormGroup.Render column={true} title={t('z677_term')} size={size}>
            <DateRangeInputContainer
              changeDateFrom={changeDate('from')}
              changeDateTo={changeDate('to')}
            >
              {({ DateFromInput, DateToInput }) => (
                <Flex row={true}>
                  <Flex
                    column={true}
                    alignStart={true}
                    style={{
                      flexBasis: '50%',
                      minWidth: '50%',
                      paddingRight: `${10 * size}px`,
                    }}
                  >
                    <DateFromInput
                      label={t('reservation_from')}
                      name={'dateFrom'}
                      disabled={airbnb && airbnb !== '0'}
                      width={'100%'}
                      size={size}
                      sized={!isDesktop}
                      offsetLeft={window.innerWidth < 1330 ? 50 : 0}
                      selectedDay={new Date()}
                      formatDate={formatDate}
                      isSmallReservation={true}
                      rangeType={RangeType.Start}
                    />
                    <VisibilityControlled field={'reservationTime'}>
                      <TimeInput.ReduxField
                        readonly={true}
                        size={size}
                        disabled={airbnb && airbnb !== '0'}
                        name={'timeFrom'}
                        width={'100%'}
                        wrapperStyle={{ marginTop: `${20 * size}px` }}
                        isSmallReservation={true}
                        onClick={onTimeClick}
                      />
                    </VisibilityControlled>
                  </Flex>
                  <Flex
                    alignStart={true}
                    column={true}
                    style={{
                      flexBasis: '50%',
                      minWidth: '50%',
                      paddingLeft: `${10 * size}px`,
                    }}
                  >
                    <DateToInput
                      label={t('reservation_to')}
                      name={'dateTo'}
                      size={size}
                      width={'100%'}
                      disabled={airbnb && airbnb !== '0'}
                      formatDate={formatDate}
                      offsetLeft={window.innerWidth < 1330 ? 50 : 0}
                      selectedDay={new Date()}
                      isSmallReservation={true}
                      sized={!isDesktop}
                      rangeType={RangeType.End}
                      withEmptyOnStart={true}
                    />

                    <VisibilityControlled field={'reservationTime'}>
                      <TimeInput.ReduxField
                        size={size}
                        readonly={true}
                        name={'timeTo'}
                        disabled={airbnb && airbnb !== '0'}
                        wrapperStyle={{ marginTop: `${20 * size}px` }}
                        isSmallReservation={true}
                        onClick={onTimeClick}
                      />
                    </VisibilityControlled>
                  </Flex>
                </Flex>
              )}
            </DateRangeInputContainer>
            <VisibilityControlled field={'daysCount'}>
              <Flex full={true}>
                <NumberInput.ReduxField
                  size={size}
                  label={t('z677_number_of_nights')}
                  name={'daysCount'}
                  onChange={changeDate('days')}
                  minValueForNumber={1}
                  disabled={airbnb && airbnb !== '0'}
                  onlyInteger={true}
                  maxValueForNumber={999}
                  wrapperFull={true}
                  style={{ maxWidth: '100%' }}
                  wrapperStyle={{ maxWidth: '100%' }}
                  isSmallReservation={true}
                />
              </Flex>
            </VisibilityControlled>
          </FormGroup.Render>

          <FormGroup.Render
            size={size}
            column={true}
            title={t('reservation_stage')}
          >
            <Flex column={true}>
              <DefaultStatusRedux
                id={this.props.id}
                prepaidType={initialValues.prepaymentType}
                airbnb={airbnb}
                size={size}
                formName={
                  type === 'update' ? FormNames.Update : FormNames.Create
                }
              />
            </Flex>
            <VisibilityControlled field={'setCustomColor'}>
              <Flex
                row={true}
                alignStart={true}
                style={{
                  marginTop: `${20 * size}px`,
                  zIndex: 0,
                }}
              >
                <ColorInputWithColors
                  width={'100%'}
                  isHorizontal={true}
                  size={size}
                  name={'customColor'}
                  disabled={isLite}
                  noAlign={true}
                />
              </Flex>
            </VisibilityControlled>
          </FormGroup.Render>

          <FormGroup.Render
            size={size}
            column={true}
            title={t('number_of_people')}
          >
            <Flex row={true} alignStart={true}>
              <NumberInput.ReduxField
                size={size}
                onChange={this.checkCountOfAdults}
                name={'adultsCount'}
                minValueForNumber={1}
                maxValueForNumber={20}
                label={t('reservation_adults')}
                width={'100%'}
                style={{ maxWidth: '100%' }}
                isSmallReservation={true}
                onlyInteger
                countIsToLarge={countOfPersonsIsToLarge()}
                inputStyle={{ maxWidth: '100%', minWidth: '0px' }}
              />
              <NumberInput.ReduxField
                size={size}
                minValueForNumber={0}
                maxValueForNumber={20}
                onChange={this.onChange}
                name={'childrenCount'}
                label={t('reservation_children')}
                width={'100%'}
                style={{ maxWidth: '100%' }}
                isSmallReservation={true}
                onlyInteger
                countIsToLarge={countOfPersonsIsToLarge()}
                inputStyle={{ maxWidth: '100%', minWidth: 0 }}
                wrapperStyle={{ marginLeft: `${20 * size}px` }}
              />
            </Flex>
            <Flex row={true}>
              <FieldArray
                name={'children'}
                component={ChildrenAge as React.ComponentType<any>}
              />
            </Flex>
          </FormGroup.Render>
          <FormGroup.Render size={size} column={true} title={t('room')}>
            <Flex style={{ zIndex: 10 }} column={true}>
              <RoomSelect
                wrapperStyle={{
                  cursor:
                    initialValues.roomId !== undefined &&
                    initialValues.roomId !== null
                      ? 'not-allowed'
                      : 'unset',
                  marginTop: `${10 * size}px`,
                }}
                isSmallReservation={true}
                name={'roomId'}
                width={'100%'}
                size={size}
                noOptionsMessage={t('search_not_found')}
                placeholder={t('select')}
                roomId={initialValues.roomId}
                initialFrom={initialValues.dateFrom}
                initialTo={initialValues.dateTo}
                type={type}
                disabled={
                  initialValues.roomId !== undefined &&
                  initialValues.roomId !== null
                }
                change={change}
                className={'reservation-form-room-select'}
              />
            </Flex>
          </FormGroup.Render>
        </Flex>
        <Desktop>
          <Flex
            style={{ flexBasis: `${25 * size}px`, minWidth: `${25 * size}px` }}
          />
        </Desktop>
        <Flex column={true} full={true}>
          <FormGroup.Render size={size} title={t('reservation_price')}>
            <Flex column={true} full={true}>
              <Flex column={true} style={{ marginBottom: `${10 * size}px` }}>
                <MainInputWithCurrency
                  alignCurrency={true}
                  size={size}
                  width={'100%'}
                  name={'endPrice'}
                  label={t('reservation_price_final')}
                  isSmallReservation={true}
                  onCalculate={!isSaving && calculate}
                  onRefresh={!isSaving && refresh}
                  isSideButtonDisabled={isLite}
                />
              </Flex>

              <VisibilityControlled field={'pricePerPerson'}>
                <PriceInputWithToolTip
                  alignCurrency={true}
                  size={size}
                  width={'100%'}
                  label={t('z677_price_per_person')}
                  name={'pricePerPerson'}
                  isSmallReservation={true}
                />
              </VisibilityControlled>

              <VisibilityControlled field={'pricePerDay'}>
                <PriceInputWithToolTip
                  alignCurrency={true}
                  size={size}
                  width={'100%'}
                  label={t('reservation_price_per_day')}
                  name={'pricePerDay'}
                  isSmallReservation={true}
                />
              </VisibilityControlled>

              <VisibilityControlled field={'advancePayment'}>
                <ExpandableSection
                  size={size}
                  label={t(
                    initialValues.prepaidType
                      ? Number(initialValues.prepaidType) ===
                        PrepaidType.Advance
                        ? 'reservation_advance_payment'
                        : 'pre_payment_form_down'
                      : Number(prepaidType) === PrepaidType.Advance
                      ? 'reservation_advance_payment'
                      : 'pre_payment_form_down',
                  )}
                  fields={[
                    'advancePaymentDate',
                    'advancePaymentType',
                    'timeToPayDays',
                  ]}
                  alwaysVisible={
                    <PriceInputWithCurrency
                      alignCurrency={true}
                      width={'100%'}
                      size={size}
                      name={'advancePaymentPrice'}
                      isSmallReservation={true}
                      withAfterIcon={<FiSettings size={16 * size} />}
                      onAfterIconClick={goToAdvanceSettings}
                    />
                  }
                >
                  <VisibilityControlled field={'advancePaymentDate'}>
                    <DateInput.ReduxField
                      size={size}
                      isCenterOnMobile={!isDesktop}
                      isClear={true}
                      selectedDay={new Date()}
                      isSmallReservation={true}
                      withEmptyOnStart={true}
                      formatDate={formatDate}
                      name={'advancePriceDate'}
                      label={t('date_of_payment')}
                      width={'100%'}
                      sized={!isDesktop}
                      disableMobileResizing={isMobile}
                      wrapperStyle={{ margin: `${10 * size}px 0px` }}
                      newDatePicker={true}
                    />
                  </VisibilityControlled>

                  <VisibilityControlled field={'advancePaymentType'}>
                    <SelectWithDictionary
                      width={'100%'}
                      zIndex={120}
                      size={size}
                      placeholder={t('select')}
                      noOptionsMessage={t('registration_none')}
                      label={t('payment_type')}
                      name={'advancePaymentType'}
                      withConfig={false}
                      onIconClick={redirect}
                      isSmallReservation={true}
                      menuPlacement={'auto'}
                    />
                  </VisibilityControlled>

                  <VisibilityControlled field={'timeToPayDays'}>
                    <Flex column={true}>
                      <GroupTitle size={size} style={{ marginBottom: 0 }}>
                        {t('z677_waiting_for_transfer')}
                      </GroupTitle>
                      <Flex row={true} alignEnd={true}>
                        <NumberInput.ReduxField
                          size={size}
                          width={'40%'}
                          label={t('reservation_number_of_days')}
                          name={'timeToPayDays'}
                          minValueForNumber={0}
                          maxValueForNumber={999}
                          isSmallReservation={true}
                        />
                        <div style={{ margin: `0px ${10 * size}px` }} />
                        <DateInput.ReduxField
                          size={size}
                          isCenterOnMobile={!isDesktop}
                          isSmallReservation={true}
                          isClear={true}
                          formatDate={formatDate}
                          withEmptyOnStart={true}
                          readonly={true}
                          name={'timeToPayDate'}
                          width={'60%'}
                          label={t('reservation_to')}
                          sized={!isDesktop}
                          disableMobileResizing={isMobile}
                          newDatePicker={true}
                        />
                      </Flex>
                    </Flex>

                    <Flex column={true}>
                      <GroupTitle
                        size={size}
                        style={{ marginBottom: 0, marginTop: `${20 * size}px` }}
                      >
                        {t('z677_waiting_for_payment_time')}
                      </GroupTitle>
                      <Flex row={true} alignEnd={true}>
                        <NumberInput.ReduxField
                          size={size}
                          width={'40%'}
                          label={t('reservation_number_of_days')}
                          isSmallReservation={true}
                          minValueForNumber={0}
                          maxValueForNumber={999}
                          name={'timeToWaitDays'}
                        />
                        <div style={{ margin: `0px ${10 * size}px` }} />
                        <DateInput.ReduxField
                          size={size}
                          isCenterOnMobile={!isDesktop}
                          formatDate={formatDate}
                          readonly={true}
                          isClear={true}
                          withEmptyOnStart={true}
                          name={'timeToWaitDate'}
                          isSmallReservation={true}
                          width={'60%'}
                          label={t('reservation_to')}
                          sized={!isDesktop}
                          disableMobileResizing={isMobile}
                          newDatePicker={true}
                        />
                      </Flex>
                    </Flex>
                  </VisibilityControlled>
                </ExpandableSection>
              </VisibilityControlled>

              <VisibilityControlled field={'onSitePayment'}>
                <ExpandableSection
                  size={size}
                  label={t('reservation_pay_on_place')}
                  fields={['onSitePaymentDate', 'onSitePaymentType']}
                  alwaysVisible={
                    <PriceInputWithCurrency
                      width={'100%'}
                      size={size}
                      alignCurrency={true}
                      name={'payOnPlacePrice'}
                      isSmallReservation={true}
                      disabled={true}
                      wrapperStyle={{ cursor: 'not-allowed' }}
                      allowNegative={true}
                    />
                  }
                >
                  <VisibilityControlled field={'onSitePaymentDate'}>
                    <DateInput.ReduxField
                      size={size}
                      formatDate={formatDate}
                      isClear={true}
                      withEmptyOnStart={true}
                      isCenterOnMobile={!isDesktop}
                      selectedDay={new Date()}
                      label={t('date_of_payment')}
                      isSmallReservation={true}
                      width={'100%'}
                      name={'payOnPlaceDate'}
                      sized={!isDesktop}
                      disableMobileResizing={isMobile}
                      newDatePicker={true}
                    />
                  </VisibilityControlled>
                  <VisibilityControlled field={'onSitePaymentType'}>
                    <SelectWithDictionary
                      width={'100%'}
                      size={size}
                      placeholder={t('select')}
                      noOptionsMessage={t('registration_none')}
                      label={t('payment_type')}
                      withConfig={false}
                      onIconClick={redirect}
                      isSmallReservation={true}
                      style={{ marginBottom: `${20 * size}px` }}
                      name={'payOnPlacePaymentType'}
                      menuPlacement={'auto'}
                    />
                  </VisibilityControlled>
                </ExpandableSection>
              </VisibilityControlled>

              <VisibilityControlled field={'deposit'}>
                <Flex column={true} style={{ margin: `${10 * size}px 0px` }}>
                  <PriceInputWithCurrency
                    width={'100%'}
                    size={size}
                    alignCurrency={true}
                    label={t('reservation_deposit')}
                    name={'deposit'}
                    isSmallReservation={true}
                  />
                </Flex>
              </VisibilityControlled>

              <VisibilityControlled field={'localTax'}>
                <>
                  <LocalTaxInput
                    alignCurrency={true}
                    size={size}
                    width={'100%'}
                    label={t('reservation_local_tax')}
                    name={'localTax'}
                    isSmallReservation={true}
                  />
                  <Flex
                    style={{ marginLeft: 'auto', marginTop: `${10 * size}px` }}
                  >
                    <SquareCheckbox.ReduxField
                      size={size}
                      name={'withoutTax'}
                      fireEventOnClick={true}
                      noSpacing={true}
                      isHorizontal={true}
                      label={t('reservation_local_tax_included')}
                      wrapperStyle={{ zIndex: 0 }}
                      isReverse={true}
                    />
                  </Flex>
                </>
              </VisibilityControlled>
            </Flex>
          </FormGroup.Render>
        </Flex>
        <Desktop>
          <Flex
            style={{ flexBasis: `${25 * size}px`, minWidth: `${25 * size}px` }}
          />
        </Desktop>
        <Flex column={true} full={true}>
          <VisibilityControlled field={'additionalServices'}>
            <FormGroup.Render
              size={size}
              isDisabled={isLite}
              withPopoverIcon={isLite}
              withoutBtnPadding={true}
              withButton={allowReservationEdit}
              toggle={!isSaving && toggleServicesModal(true)}
              title={t('z2736_label_additional_services')}
            >
              <FieldArray
                name={'services'}
                component={Services as any}
                props={{
                  allowReservationEdit: allowReservationEdit && !isLite,
                  formName:
                    type === 'update'
                      ? 'update_reservation'
                      : 'create_reservation',
                  size,
                }}
                rerenderOnEveryChange={true}
              />
            </FormGroup.Render>
          </VisibilityControlled>
        </Flex>
      </Flex>
    )
  }
}

export default withIsHorizontal(
  withIsDesktop(withSize(withTranslation()(ReservationTab))),
)

export interface IReservationTabProps {
  initialValues: ICreateReservationFormValues
  formatDate: string
  timeTo?: string
  airbnb?: string
  timeFrom?: string
  prepaidType?: PrepaidType
  roomSize?: number | string
  change: any
  type?: string
  isSaving?: boolean
  unavailableDates?: Date[]
  id?: string
  allowConfig: boolean
  allowReservationEdit: boolean
  isLite?: boolean
}

export interface IReservationTabDispatch {
  toggleServicesModal: (toggle: boolean) => () => void
  changeChildren: (count: number) => void
  changeAdultsCount: (count: number) => void
  changeDates: (name: string, e: Date | number) => void
  calculate: () => void
  refresh: () => void
  firePricingError: () => void
}
